import { useEffect, useState } from "react";
import { Botao, Faixa } from "./comuns.jsx";
import { ICONES, Icone, s } from "../visual.jsx";
import { useAcoes, useEstado, usePode } from "../estado.jsx";
import {
  ESPERA_DO_COMPOSER_MS,
  conectado,
  desdeQuando,
  foraDoAr,
  motivoDoFora,
  numeroLegivel,
  sabemos,
} from "../whatsapp-estado.js";
import { useNavegacao } from "../navegacao.jsx";

/**
 * O aviso de que o WhatsApp do escritório está fora do ar.
 *
 * Nasceu da queda de 13/09: o aparelho caiu às 16:41 e o escritório descobriu às
 * 18:28, testando à mão. Durante as duas horas a mesa ficou idêntica à de um dia
 * normal — nada chegava (o que parece cliente calado) e o que saía era gravado
 * com `entrega: "ensaio"`, com o aviso aparecendo só DEPOIS de apertar enviar,
 * uma mensagem por vez. Nada na tela dizia a única coisa que importava: o
 * canal está fora.
 *
 * Duas aparições, e são papéis diferentes:
 * - `faixa`, no alto da mesa — a notícia, para quem ainda não escreveu nada;
 * - `composer`, acima da barra de escrever — a CONSEQUÊNCIA, colada no lugar
 *   onde ela vai doer, no idioma da faixa de nota interna que já mora ali.
 *
 * Não é legenda (ver a regra da barra de escrever): só existe quando o canal
 * está fora, e some sozinho quando ele volta.
 *
 * PARENTE, E DE PROPÓSITO DIFERENTE, da `FaixaCanal`. Aquela é o canal de
 * eventos caindo: dura segundos, volta sozinha e não tem o que fazer — por isso
 * flutua, com `pointer-events-none`. Esta dura horas e EXIGE uma pessoa, então
 * ocupa lugar no leiaute e leva botão: pílula flutuante que não se pode tocar
 * nem dispensar, pendurada sobre a conversa a tarde inteira, é pior que
 * silêncio. Não unifique as duas sem desfazer essa diferença.
 *
 * Sem o `Alert` do registro na faixa, e isto é escolha: ele é um cartão
 * (`rounded-2xl`, borda, grade) e a faixa é uma tira de ponta a ponta — usá-lo
 * aqui seria sobrescrever raio, borda, respiro e grade até não sobrar nada dele.
 * O bloco do composer segue a faixa de NOTA INTERNA, que é o vizinho dele.
 */

/**
 * Reavalia o tempo enquanto o canal está fora — e só enquanto.
 *
 * `foraDoAr` compara com o relógio, então sem isto uma queda que começou agora
 * ficaria invisível até o próximo evento ou a próxima troca de tela. Com o canal
 * de pé não há temporizador nenhum.
 *
 * Devolve o AGORA em vez de só forçar o repinte: `Date.now()` chamado no meio do
 * render é função impura — duas superfícies na mesma tela leriam relógios
 * diferentes, e o lint barra com razão. Uma leitura por repinte, a mesma para
 * todo mundo.
 */
function useRelogioDaQueda(ativo) {
  const [agora, marcar] = useState(() => Date.now());
  useEffect(() => {
    if (!ativo) return undefined;
    const t = setInterval(() => marcar(Date.now()), 15_000);
    return () => clearInterval(t);
  }, [ativo]);
  return agora;
}

/**
 * O ESTADO DO WHATSAPP, uma leitura só para a mesa inteira.
 *
 * Era o defeito que sobrou depois do primeiro conserto: cada superfície montava
 * a própria resposta a partir de fontes diferentes — a faixa e o composer liam o
 * `wa` que vem com `/api/mesa`, os Ajustes liam `conexaoWa` de
 * `/api/whatsapp/conexao`, e cada um traduzia a palavra do banco no próprio
 * vocabulário. Duas fontes e três traduções é como uma delas passa a discordar
 * das outras — e num aviso cuja função é dizer a verdade sobre o canal, discordar
 * é o defeito inteiro de volta.
 *
 * A precedência é EXPLÍCITA: `conexaoWa` ganha quando existe, porque só quem
 * alcança a automação o recebe e ele é mais fresco (a tela de Ajustes o rebusca
 * a cada evento). Para quem atende ele nunca chega — e aí vale o `wa` da mesa,
 * que chega para todo mundo e é mantido ao vivo pelo mesmo evento.
 */
export function useWhatsapp() {
  const { wa, conexaoWa } = useEstado();
  const pode = usePode();
  /*
   * Uma fonte INTEIRA vence a outra — nunca um campo de cada.
   *
   * Misturar campo a campo (`conexaoWa?.codigo ?? wa?.codigo`) juntaria o estado
   * fresco de uma leitura com o código de outra, e o aviso diria "reconectando"
   * mostrando o motivo da queda anterior. `conexaoWa` ganha quando existe porque
   * é mais fresco (a tela de Ajustes o rebusca a cada evento) e porque nele o
   * SOCKET VIVO tem a última palavra — `provedores.estado()` no servidor. Quem
   * atende não o recebe (exige `automacao:ver`), e aí vale o `wa` da mesa, que
   * chega para todo mundo e o mesmo evento mantém ao vivo.
   *
   * Campo a campo e não o objeto todo espalhado: `conexaoWa` traz `pronta`,
   * `capacidades` e o mapa de credencial, que não são estado do canal.
   */
  const fonte = conexaoWa?.estado ? conexaoWa : wa;
  const atual = {
    estado: fonte?.estado ?? "",
    detalhe: fonte?.detalhe ?? null,
    codigo: fonte?.codigo ?? null,
    desde: fonte?.desde ?? null,
    numero: fonte?.numero ?? null,
  };
  const agora = useRelogioDaQueda(sabemos(atual) && !conectado(atual));
  return {
    ...atual,
    sabemos: sabemos(atual),
    conectado: conectado(atual),
    /* Duas carências, uma regra: ver `whatsapp-estado.js`. */
    fora: foraDoAr(atual, agora),
    foraParaEscrever: foraDoAr(atual, agora, ESPERA_DO_COMPOSER_MS),
    motivo: motivoDoFora(atual),
    /* Há motivo de verdade a mostrar? Sem isto, cada superfície repetia a
       paráfrase do próprio título quando o provedor não disse nada. */
    temMotivo: Boolean(motivoDoFora(atual)),
    tempo: desdeQuando(atual, agora),
    numeroBonito: numeroLegivel(atual.numero),
    /* Quem conserta é quem cuida da automação. Para os outros as superfícies
       dizem a QUEM pedir, e não um botão que responde "não autorizado". */
    podeMexer: pode("automacao:conexao"),
  };
}

export default function AvisoDoWhatsapp({ onde = "faixa" }) {
  const wa = useWhatsapp();
  const nav = useNavegacao();
  /* A barra de escrever tem carência CURTA: ali a pergunta é "o que eu apertar
     agora vai chegar?", e durante a reconexão a resposta é não — a mensagem
     grava com `entrega: "ensaio"` e não sai. */
  if (!(onde === "composer" ? wa.foraParaEscrever : wa.fora)) return null;

  if (onde === "composer")
    return (
      /* A faixa da casa no tom de aviso — a mesma geometria da nota interna, da
         citação e da agendada, que são as vizinhas dela acima da barra. O
         `role` fica no invólucro porque a faixa não repassa atributo. */
      <div role="status">
        <Faixa
          tom="aviso"
          icone={ICONES.risco}
          titulo="Não vai chegar ao cliente"
          classe="animate-[vFade_var(--t-curta)_var(--e-entra)_both]"
        >
          {/* O que a cor e o ícone NÃO dizem: por quê. É o que falta para alguém
              agir em vez de tentar de novo. */}
          {wa.temMotivo ? `${wa.motivo}. ` : ""}O que você mandar fica gravado aqui e sai quando ele voltar.
        </Faixa>
      </div>
    );

  return (
    /*
     * UMA LINHA, em qualquer largura — e é aqui que a primeira versão quebrou.
     *
     * Ela era `flex-wrap` com o motivo em `flex-1` ao lado de um botão de
     * largura fixa: num tablet de 760px o motivo ficava com uns 60px e quebrava
     * numa PALAVRA POR LINHA, virando uma faixa de 290px que empurrava a
     * conversa para fora da tela.
     *
     * Agora nada compete: o texto é um bloco só que trunca (`truncate`), o botão
     * é `flex-none`, e o motivo — que é o detalhe, não a notícia — some abaixo de
     * 640px, onde não cabe sem estragar a linha. Quem precisa dele em tela
     * pequena abre o cartão do resumo, que tem o texto inteiro.
     */
    <div
      role="status"
      className="flex flex-none animate-[vFade_var(--t-curta)_var(--e-entra)_both] items-center gap-2.5 border-b border-terra/28 bg-terra/10 px-3.5 py-[6px]"
    >
      <Icone de={ICONES.risco} tamanho={16} cor="var(--color-terra-escura)" estilo={s("flex:none")} />
      <div className="min-w-0 flex-1 truncate text-corpo">
        <span className="font-semibold text-terra-escura">
          {wa.estado === "erro" ? "WhatsApp parou de tentar" : "WhatsApp do escritório fora do ar"}
          {wa.tempo ? ` ${wa.tempo}` : ""}
        </span>
        {/* O motivo é DETALHE: entra na mesma linha quando sobra largura e sai
            quando não sobra, em vez de espremer a notícia. */}
        {wa.temMotivo ? <span className="ml-2 hidden text-tinta-suave min-[640px]:inline">{wa.motivo}</span> : null}
      </div>
      {wa.podeMexer ? (
        <Botao
          rotulo="Reconectar"
          tamanho="mini"
          aoTocar={() => nav.ir("/ajustes?secao=whatsapp")}
          /* No dedo o botão cresce de verdade, e não só o alvo invisível: 28px de
             botão visível numa faixa de alerta é o que a pessoa erra com pressa. */
          classe="alvo border-terra/40 bg-transparent text-terra-escura hover:bg-terra/8 hover:text-terra-escura pointer-coarse:min-h-toque pointer-coarse:px-3.5"
        />
      ) : (
        <span className="hidden flex-none text-apoio text-tinta-suave/80 min-[860px]:inline">
          Quem reconecta é quem cuida da Automação.
        </span>
      )}
    </div>
  );
}

/* ============================================== o cartão do resumo da mesa */

/**
 * O estado do WhatsApp no RESUMO da mesa — e o QR ali mesmo quando ele cai.
 *
 * O resumo é onde a pessoa chega: quem abre a mesa, quem fecha uma conversa,
 * quem recarrega. Era a tela com mais espaço e menos informação operacional da
 * casa — e em 13/09 foi a tela que alguém olhou durante duas horas sem descobrir
 * que o canal estava fora.
 *
 * TRÊS estados, e nunca dois deles ao mesmo tempo:
 *
 * 1. conectado — uma linha discreta, com o NÚMERO (estado normal não grita, mas
 *    selo verde sem número não se confere: aparelho errado pareado passa
 *    batido);
 * 2. tentando — uma linha ÂMBAR, "reconectando", enquanto a carência corre.
 *    Esta linha existe por causa de um defeito real: com a carência respondendo
 *    "não está fora", o cartão concluía "então está conectado" e pintava o ponto
 *    verde durante o laço de reconexão. Não saber ainda é um estado, e ele se
 *    escreve;
 * 3. fora — o cartão, com o motivo e o caminho de volta INTEIRO dentro dele: o
 *    QR aparece aqui, não atrás de duas telas.
 *
 * Quem não cuida da automação não ganha botão que responde "não autorizado" —
 * ganha o nome de quem resolve.
 */
export function CartaoDoWhatsapp() {
  const wa = useWhatsapp();
  const { qrWa, conexaoWa } = useEstado();
  const { conectarWa, novoQrWa } = useAcoes();

  /* Sem o dado (provedor que não carimba, mesa antiga) o cartão não existe:
     inventar "conectado" é pior que calar. */
  if (!wa.sabemos) return null;

  const podeMexer = wa.podeMexer;

  if (wa.conectado)
    return (
      <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5">
        <span aria-hidden="true" className="size-2 flex-none rounded-full bg-verde-vivo" />
        <span className="text-apoio text-tinta-suave">WhatsApp conectado{wa.numero ? ` · ${wa.numeroBonito}` : ""}</span>
      </div>
    );

  const tempo = wa.tempo;

  /* Ainda na carência: não está conectado, mas também não é hora de assustar. A
     linha diz o que É — "reconectando" —, que é o que faltava. */
  if (!wa.fora)
    return (
      <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-ambar/40 bg-ambar/10 px-3 py-1.5">
        <span
          aria-hidden="true"
          className="size-2 flex-none animate-[vRespira_1.4s_ease-in-out_infinite] rounded-full bg-ambar"
        />
        <span className="text-apoio font-semibold text-ambar-escuro">Reconectando o WhatsApp…</span>
      </div>
    );

  /* Com pareamento vivo, "Conectar" reconecta e NÃO mostra QR — é o certo no dia
     a dia. Sem pareamento (o 401 apagou a credencial) ele já traz o QR. O
     segundo botão é para o dia em que reconectar não resolve. */
  const pareado = Boolean(conexaoWa?.pareado ?? wa.numero);

  return (
    <div className="mb-6 w-full max-w-[420px] rounded-xl border border-terra/38 bg-terra/8 p-[16px_18px] text-left">
      <div className="flex items-center gap-2">
        <Icone de={ICONES.risco} tamanho={20} cor="var(--color-terra-escura)" />
        <span className="text-forte font-bold text-terra-escura">
          {/* "Parou de tentar" e "desconectado" pedem coisas diferentes de quem
              lê: o primeiro só sai do lugar com um toque. Dizer "desconectado"
              nos dois casos deixava a pessoa esperando um servidor que já
              tinha desistido. */}
          {wa.estado === "erro" ? "WhatsApp parou de tentar" : "WhatsApp desconectado"}
          {tempo ? ` ${tempo}` : ""}
        </span>
      </div>
      <div className="mt-1.5 text-corpo leading-[1.5] text-tinta-suave">
        {wa.temMotivo ? `${wa.motivo}. ` : ""}Nada entra, e o que você escrever fica guardado aqui sem sair.
      </div>

      {podeMexer ? (
        <>
          {qrWa ? (
            <div className="mt-3.5 text-center">
              {/* O QR no tamanho de LER pela câmera do celular a um braço de
                  distância — miniatura que obriga a aproximar o rosto da tela é
                  o mesmo que não ter QR. */}
              <img src={qrWa} alt="QR para parear o WhatsApp" className="mx-auto size-[196px] rounded-xl bg-creme p-1.5" />
              <div className="mt-2 text-apoio leading-[1.45] text-tinta-suave">
                No celular: <strong>Aparelhos conectados → Conectar aparelho</strong>. O QR vive poucos segundos — se apagar,
                toque em gerar de novo.
              </div>
            </div>
          ) : null}
          <div className="mt-3.5 flex flex-wrap gap-2">
            {/* Reconectar é ação da casa, não mensagem ao cliente: tinta, e o
                terra fica para o que sai para fora. */}
            <Botao
              rotulo={qrWa ? "Gerar outro QR" : wa.estado === "erro" ? "Tentar de novo" : "Conectar agora"}
              variante="destaque"
              aoTocar={conectarWa}
            />
            {pareado ? (
              <Botao
                rotulo="Ler QR de novo"
                aoTocar={novoQrWa}
                classe="border-terra/40 bg-transparent text-terra-escura hover:bg-terra/8 hover:text-terra-escura"
              />
            ) : null}
          </div>
        </>
      ) : (
        /* Sem permissão o cartão não vira beco: diz QUEM libera, que é a mesma
           regra do vazio por falta de permissão. */
        <div className="mt-3 text-apoio text-tinta-suave">
          Quem reconecta é quem cuida da <strong>Automação</strong> — chame essa pessoa para ler o QR.
        </div>
      )}
    </div>
  );
}
